import common from '../../util/scss/common.module.scss';
import styles from './ConfirmFamilyAccount.module.scss';
import CreateFamilyAccountContext, {
    TFirstPartyUser,
    ECreateFamilyAccountUiState,
} from '../../contexts/CreateFamilyAccountContext';
import language from '../../util/language';
import { getInitials } from '../../util/helpers';
import LegalAgreements from '../LegalAgreements/LegalAgreements';
import LegalAgreementsCheckboxes from '../LegalAgreementsCheckboxes/LegalAgreementsCheckboxes';
import PrimaryUser from '../PrimaryUser/PrimaryUser';
import { EFamilyAccountType, EMixpanelEvent } from '../../util/constants';
import BackCardLayout from '../../layout/BackCardLayout/BackCardLayout';
import { IconClick, IconFamily2 } from '../../icons/Icons';
import { replacePhrase } from '../../util/helpers';
import GlobalContext from '../../contexts/GlobalContext';
import { validateEmail } from '../../util/validation/formValidation';
import UserExistsBanner from '../UserExistsBanner/UserExistsBanner';
import API from '../../api/api';
import CoBranding from '../CoBranding/CoBranding';
import { FC, ReactElement, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { Avatar, Box, Divider, Stack, TextField, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import mixpanel from 'mixpanel-browser';

enum EConfirmFamilyUiState {
    loading,
    ready,
    retryEmail,
    success,
}

type TConfirmAccountProps = {
    confirmFamilyUiState: EConfirmFamilyUiState;
    onSubmit: () => void;
};

type TMigrateConfirmAccountProps = TConfirmAccountProps & {
    userExists: boolean;
    setConfirmFamilyUiState: (uiState: EConfirmFamilyUiState) => void;
};

const ConfirmCreateAccount: FC<TConfirmAccountProps> = (props) => {
    const {
        familyItems,
        primaryUser,
        familyAccountType,
        tosAgreed,
        setTosAgreed,
        marketingOptIn,
        setMarketingOptIn,
        tosIsValid,
    } = useContext(CreateFamilyAccountContext);
    const { confirmFamilyUiState, onSubmit } = props;
    const user = primaryUser as TFirstPartyUser;
    const theme = useTheme();

    const handleSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        mixpanel.track(EMixpanelEvent.interacted, {
            key: 'consumer_create_account_family',
            marketingOptIn: marketingOptIn,
        });
        onSubmit();
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_create_account_family_confirm' });
    }, []);

    return (
        <PageLayoutV2>
            <Box sx={{ textAlign: { lg: 'center' } }}>
                <IconFamily2 fillColor={theme.palette.primary.main} />
            </Box>
            <ScreenHeader title={language.createFamilyAccount.confirmHeader} margin={1} />
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Typography component={'h3'} variant="subtitle2" sx={{ my: 2 }}>
                {language.createFamilyAccount.primaryUser.title}
            </Typography>
            <Stack direction={'row'} alignItems={'center'} data-testid="primary-user">
                <Avatar sizes={'60px'} sx={{ background: theme.palette.primary.main, mr: 2 }}>{`${getInitials(
                    user.firstName,
                    user.lastName,
                )}`}</Avatar>
                <Box>
                    <Typography
                        component={'p'}
                        variant={'subtitle1'}
                    >{`${user.firstName} ${user.lastName} (${language.common.you})`}</Typography>
                    {user.email && <Typography component={'p'} variant={'body2'}>{`${user.email}`}</Typography>}
                </Box>
            </Stack>
            {familyItems.length && (
                <Typography component={'h3'} variant="subtitle2" sx={{ my: 2 }}>
                    {language.createFamilyAccount.itemsHeader}
                </Typography>
            )}
            {familyItems.map((item, index) => (
                <Stack direction={'row'} sx={{ alignItems: 'center', mt: 2 }} key={index} data-testid="family-item">
                    <Stack sx={{ width: '60px', mr: 2 }}>
                        <Avatar sizes={'40px'} sx={{ fontSize: '14px', margin: 'auto' }}>{`${getInitials(
                            item.firstName,
                            item.lastName,
                        )}`}</Avatar>
                    </Stack>
                    <p>{`${item.firstName} ${item.lastName}`}</p>
                </Stack>
            ))}

            {familyAccountType === EFamilyAccountType.firstParty ? (
                <form>
                    <Box sx={{ my: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        <LegalAgreementsCheckboxes
                            tosAgreed={tosAgreed}
                            setTosAgreed={setTosAgreed}
                            marketingOptIn={marketingOptIn}
                            setMarketingOptIn={setMarketingOptIn}
                            tosIsValid={tosIsValid}
                        />
                    </Box>
                    <LoadingButton
                        variant="contained"
                        loading={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                        type="submit"
                        onClick={handleSubmit}
                        fullWidth
                        size="large"
                    >
                        {language.createAccount.createFamilyAccountCTA}
                    </LoadingButton>
                </form>
            ) : (
                <form>
                    <LoadingButton
                        variant="contained"
                        loading={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                        disabled={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                        type="submit"
                        onClick={handleSubmit}
                        size={'large'}
                        sx={{ my: 2 }}
                        fullWidth
                    >
                        {language.createAccount.createFamilyAccountCTA}
                    </LoadingButton>
                    <LegalAgreements />
                </form>
            )}
        </PageLayoutV2>
    );
};

const MigrateAccountConfirm: FC<TMigrateConfirmAccountProps> = (props) => {
    const { coBranding } = useContext(GlobalContext);
    const theme = useTheme();
    const { createFamilyAccountUiState, familyItems, primaryUser, setPrimaryUser } =
        useContext(CreateFamilyAccountContext);
    const { confirmFamilyUiState, setConfirmFamilyUiState, onSubmit, userExists } = props;
    const user = primaryUser as TFirstPartyUser;
    const businessName = coBranding.name ? coBranding.name : language.confirmAccount.defaultBusinessName;
    const businessNameReplacePhrase = '<businessName>';
    const desc = replacePhrase(language.confirmAccount.confirm.desc, businessNameReplacePhrase, businessName);
    const [newEmail, setNewEmail] = useState('');
    const [retryEmailIsValid, setRetryEmailIsValid] = useState(true);

    const validateRetryEmail = () => {
        let valid = true;
        if (!validateEmail(newEmail)) {
            valid = false;
        }
        return valid;
    };

    const handleSubmit = () => {
        API.post.trackEvent({ event: 'Sign In || Migration | Almost Done | Button - Create Account' });
        onSubmit();
    };

    const handleSubmitRetryEmail = (event) => {
        event.preventDefault();
        if (validateRetryEmail()) {
            setPrimaryUser({ ...user, email: newEmail });
            setConfirmFamilyUiState(EConfirmFamilyUiState.retryEmail);
        } else {
            setRetryEmailIsValid(false);
        }
    };

    useEffect(() => {
        if (confirmFamilyUiState === EConfirmFamilyUiState.retryEmail) {
            onSubmit();
        }
    }, [confirmFamilyUiState]);

    useEffect(() => {
        if (createFamilyAccountUiState === ECreateFamilyAccountUiState.ready) {
            API.post.trackEvent({ event: 'Sign In || Migration | Almost Done' });
        }
        return () => {
            setConfirmFamilyUiState(EConfirmFamilyUiState.ready);
        };
    }, []);

    return (
        <BackCardLayout>
            {userExists && <UserExistsBanner />}
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <section className={common.icon} data-testid={'icon'}>
                <IconClick fillColor={theme.palette.grey[900]} />
            </section>
            <header>
                <h1 className={`${common.pageHeader} ${common.minPadding}`}>{language.confirmAccount.confirm.title}</h1>
                <h2 className={common.pageSubhead}>{desc}</h2>
            </header>
            <section className={styles.itemsContainer}>
                <PrimaryUser
                    firstName={user.firstName}
                    lastName={user.lastName}
                    email={user.email}
                    idLabel={language.common.you}
                    hasError={userExists ? true : undefined}
                />
                {familyItems.map((item, index) => (
                    <article className={styles.familyItem} key={index} data-testid="family-item">
                        <Avatar>{`${getInitials(item.firstName, item.lastName)}`}</Avatar>
                        <p>{`${item.firstName} ${item.lastName}`}</p>
                    </article>
                ))}
            </section>
            {userExists ? (
                <form>
                    <section className={styles.newEmail}>
                        <TextField
                            label={language.common.newEmail}
                            value={newEmail}
                            onChange={(event) => {
                                setNewEmail(event.target.value);
                            }}
                            error={!retryEmailIsValid}
                            helperText={!retryEmailIsValid && language.validation.email}
                            autoFocus
                            fullWidth
                        />
                    </section>
                    <section className={`${styles.ctaContainer} ${styles.minPadding}`}>
                        <LoadingButton
                            variant="contained"
                            loading={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                            disabled={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                            type="submit"
                            onClick={handleSubmitRetryEmail}
                            className={common.buttonShim}
                        >
                            {language.confirmAccount.confirm.cta}
                        </LoadingButton>
                    </section>
                </form>
            ) : (
                <section className={styles.ctaContainer}>
                    <LoadingButton
                        variant="contained"
                        loading={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                        disabled={confirmFamilyUiState === EConfirmFamilyUiState.loading}
                        type="button"
                        onClick={handleSubmit}
                        className={common.buttonShim}
                    >
                        {language.confirmAccount.confirm.cta}
                    </LoadingButton>
                </section>
            )}

            <LegalAgreements />
        </BackCardLayout>
    );
};

const ConfirmFamilyAccount: FC = () => {
    const { submitFamilyAccount, familyAccountType, createFamilyAccountUiState } =
        useContext(CreateFamilyAccountContext);
    const [confirmFamilyUiState, setConfirmFamilyUiState] = useState(EConfirmFamilyUiState.ready);

    const handleSubmitFamilyAccount = async () => {
        setConfirmFamilyUiState(EConfirmFamilyUiState.loading);
        try {
            await submitFamilyAccount();
        } catch {
            setConfirmFamilyUiState(EConfirmFamilyUiState.ready);
        }
    };

    let confirmFamilyUi: ReactElement;
    switch (familyAccountType) {
        case EFamilyAccountType.firstParty:
        case EFamilyAccountType.thirdParty:
        case EFamilyAccountType.confirmAccount:
            confirmFamilyUi = (
                <ConfirmCreateAccount
                    confirmFamilyUiState={confirmFamilyUiState}
                    onSubmit={handleSubmitFamilyAccount}
                />
            );
            break;
        default:
            throw Error('Unmatched family Account type in ConfirmFamilyAccount component');
    }

    return confirmFamilyUi;
};

export default ConfirmFamilyAccount;
